import { useState } from "react";
import { AlertCircle, FileText, Pencil, Plus, Star, Trash2 } from "lucide-react";
import {
  useProposalTemplates,
  useDeleteProposalTemplate,
  type ProposalTemplate,
} from "@/hooks/use-proposals";
import { TemplateEditorDialog } from "@/components/templates/template-editor-dialog";

// Mejoras L/I (changelog cliente): gestión de plantillas de propuesta.
// El admin puede crear, editar y eliminar templates desde la UI sin
// necesidad de un deploy. Cada plantilla queda disponible para reutilizar
// al crear una propuesta nueva.

const TEMPLATE_TYPE_LABEL: Record<string, string> = {
  TECNICA: "Técnica",
  ECONOMICA: "Económica",
  COMPLETA: "Completa",
};

export default function TemplatesPage() {
  const { data: templates = [], isLoading, isError } = useProposalTemplates();
  const deleteMutation = useDeleteProposalTemplate();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ProposalTemplate | null>(null);

  const handleNew = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const handleEdit = (template: ProposalTemplate) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  };

  const handleDelete = (template: ProposalTemplate) => {
    const confirmed = window.confirm(
      `¿Eliminar la plantilla "${template.name}"? Esta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    deleteMutation.mutate({ templateId: template.id });
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Plantillas de propuesta
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Las plantillas definen la estructura de secciones que se aplicarán
            automáticamente al crear una nueva propuesta. Crea una por cada
            tipo de licitación que gestionéis (CIMI, GES, convivencias, etc.)
            para no empezar siempre desde cero.
          </p>
        </div>
        <button
          type="button"
          onClick={handleNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Nueva plantilla
        </button>
      </div>

      {isLoading && (
        <div className="text-sm text-gray-500">Cargando plantillas…</div>
      )}

      {isError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span>Error al cargar las plantillas. Recarga la página.</span>
        </div>
      )}

      {!isLoading && !isError && templates.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h2 className="text-base font-medium text-gray-700 mb-1">
            Aún no hay plantillas
          </h2>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            Crea tu primera plantilla con las secciones del pliego que vas a
            licitar para que toda la organización parta del mismo índice.
          </p>
          <button
            type="button"
            onClick={handleNew}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Crear primera plantilla
          </button>
        </div>
      )}

      {templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-gray-900 truncate">
                      {template.name}
                    </h3>
                    {template.is_default && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700"
                        title="Plantilla por defecto"
                      >
                        <Star className="w-3 h-3" />
                        Por defecto
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">
                      {TEMPLATE_TYPE_LABEL[template.template_type] ??
                        template.template_type}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(template)}
                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    aria-label="Editar plantilla"
                    title="Editar plantilla"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(template)}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    aria-label="Eliminar plantilla"
                    title="Eliminar plantilla"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">
                  {template.sections_config.length}{" "}
                  {template.sections_config.length === 1
                    ? "sección"
                    : "secciones"}
                </p>
                <ol className="text-sm text-gray-700 space-y-0.5 list-decimal list-inside">
                  {template.sections_config.slice(0, 6).map((s, idx) => (
                    <li key={idx} className="truncate">
                      {s.title}
                    </li>
                  ))}
                  {template.sections_config.length > 6 && (
                    <li className="list-none text-xs text-gray-400 ml-4">
                      …y {template.sections_config.length - 6} más
                    </li>
                  )}
                </ol>
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateEditorDialog
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        template={editingTemplate}
      />
    </div>
  );
}
